import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Clickable, Sub, Table } from '../../performance-operations/styles';

// Analiz §16 E13 orta sağ — yapılandırılmış konu başlıkları yatay çubuk; geri bildirim amaçları (güçlü yön / gelişim alanı) ve
// takdir rozetleri AYRI dağılım. Çubuk → yetkili kayıt listesi (konu süzgeciyle). Klavye: tablo görünümünde her satır düğme.
const Part = styled.div`
  margin-bottom: 12px;
`;

const PartTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 4px;
`;

function Bars({ items, color, label, onPick }) {
  const theme = useTheme();
  const height = Math.max(60, items.length * 30 + 20);
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart data={items} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} />
          <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: theme.metrics.font.size.xs }} />
          <Tooltip />
          <Bar dataKey="count" name={label} fill={color} cursor="pointer" onClick={(d) => onPick?.((d.payload || d).key)} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function TopicsPanel({ panel, onPick }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const d = panel.data;
  const categories = (d?.categories || []).map((c) => ({ ...c, label: t(`performance.feedbackAnalytics.topic.${c.key}`) }));
  const badges = (d?.badges || []).map((c) => ({ ...c, label: t(`performance.feedbackAnalytics.topic.${c.key}`) }));
  const suppressed = !!d?.suppressionReason;
  const empty = !suppressed && [...categories, ...badges].every((x) => !x.count);
  return (
    <PanelCard
      panelKey="topics" title={t('performance.feedbackAnalytics.panel.topics')} span={5}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={empty}
      tableToggle={!suppressed} showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {suppressed ? <Sub data-state="suppressed">{t('performance.feedbackAnalytics.suppressedNote')}</Sub> : null}
      {!suppressed && table ? (
        <Table>
          <thead>
            <tr>
              <th>{t('performance.feedbackAnalytics.col.topic')}</th>
              <th>{t('performance.feedbackAnalytics.col.kind')}</th>
              <th>{t('performance.feedbackAnalytics.col.count')}</th>
            </tr>
          </thead>
          <tbody>
            {[...categories, ...badges].map((c) => (
              <tr key={c.key} data-row={c.key}>
                <td><Clickable onClick={() => onPick?.(c.key, c.kind)}>{c.label}</Clickable></td>
                <td>{t(`performance.feedbackAnalytics.kind.${c.kind}`)}</td>
                <td>{c.count}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {!suppressed && !table ? (
        <>
          <Part>
            <PartTitle>{t('performance.feedbackAnalytics.kind.category')}</PartTitle>
            <Bars items={categories} color={theme.colors.primary.main} label={t('performance.feedbackAnalytics.col.count')} onPick={(k) => onPick?.(k, 'category')} />
          </Part>
          <Part>
            <PartTitle>{t('performance.feedbackAnalytics.kind.badge')}</PartTitle>
            <Bars items={badges} color={theme.colors.secondary.main} label={t('performance.feedbackAnalytics.col.count')} onPick={(k) => onPick?.(k, 'badge')} />
          </Part>
        </>
      ) : null}
    </PanelCard>
  );
}
